import { api, toast, modalConfirm } from '../state.js';
import { esc, badge, formatDateTime, sortRows, sortableHead, toggleSort, bindSortable } from '/core/js/utils.js';
import { installedModules } from '/core/js/modules.js';

/**
 * <arc-alerts> — what people should see, every module's in one list: open
 * first, by severity; acknowledged and resolved behind their tabs. A Module
 * column and filter; an alert's link goes to the thing it is about (a run,
 * a server, a peer). Acknowledge and Resolve on each row; "Acknowledge all
 * open" and "Resolve all open" on the card; Remove on a resolved one. The
 * address can narrow it (?module=, ?ctx_server_id=, ?ctx_peer_id=, …).
 * Refreshes every 30 s while nothing is open. The platform's since
 * WIREGUARD-PLAN 1.5 (W8); it was the backups module's.
 */
const STATUSES = [['open', 'Open'], ['acknowledged', 'Acknowledged'], ['resolved', 'Resolved'], ['', 'All']];
const RANK = { critical: 0, warning: 1, info: 2 };
const PAGE = 100;

export function severityTag(severity) {
  const s = String(severity ?? '').toLowerCase();
  return badge(s === 'critical' ? 'failed' : s === 'info' ? 'info' : 'warning', s || 'warning');
}

function timeAgo(value) {
  if (!value) return '';
  const d = new Date(typeof value === 'string' && !/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value.replace(' ', 'T') + 'Z' : value);
  if (isNaN(d)) return '';
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}
const plural = (n, word, words = `${word}s`) => `${Number(n).toLocaleString('en-US')} ${Number(n) === 1 ? word : words}`;

class ArcAlerts extends HTMLElement {
  #items = [];
  #total = 0;
  #offset = 0;
  #status = 'open';
  #severity = '';
  #module = '';
  #scope = {};
  #timer;
  #sortKey = 'opened';
  #sortDir = 'desc';
  #columns = [
    { key: 'severity', label: 'Severity', type: 'number', get: a => RANK[String(a.severity).toLowerCase()] ?? 9 },
    { key: 'module',   label: 'Module',   get: a => a.module },
    { key: 'title',    label: 'What',     get: a => a.title },
    { key: 'where',    label: 'Where',    get: a => a.subject || '' },
    { key: 'opened',   label: 'Opened',   type: 'date', get: a => a.opened_at },
    { key: 'status',   label: 'Status' },
    { key: 'by',       label: 'Handled',  sortable: false },
    { key: 'act',      label: '',         sortable: false },
  ];

  set ctx(c) { this._ctx = c; }

  connectedCallback() {
    this.classList.add('page-wide');
    const q = this._ctx?.query ?? {};
    this.#scope = Object.fromEntries(Object.entries(q).filter(([k]) => k.startsWith('ctx_')));
    if (q.status !== undefined) this.#status = q.status;
    if (q.module) this.#module = q.module;
    const modules = installedModules().map(m => m.name);
    this.innerHTML = `
      <div class="page-header">
        <div class="detail-title"><h1>Alerts</h1></div>
        <div class="page-header-actions">
          <button type="button" class="btn btn-secondary btn-sm" data-all="acknowledged">Acknowledge all open</button>
          <button type="button" class="btn btn-secondary btn-sm" data-all="resolved">Resolve all open</button>
        </div>
      </div>
      <div class="page-toolbar">
        <div class="tab-group" role="tablist">
          ${STATUSES.map(([v, l]) => `<button type="button" class="tab" data-status="${v}" aria-pressed="${v === this.#status ? 'true' : 'false'}">${l}</button>`).join('')}
        </div>
        <select data-f="module" class="form-input" aria-label="Module">
          <option value="">Every module</option>
          ${modules.map(m => `<option value="${esc(m)}" ${m === this.#module ? 'selected' : ''}>${esc(m)}</option>`).join('')}
          ${this.#module && !modules.includes(this.#module) ? `<option value="${esc(this.#module)}" selected>${esc(this.#module)}</option>` : ''}
        </select>
        <select data-f="severity" class="form-input" aria-label="Severity">
          <option value="">Any severity</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
      </div>
      <div data-slot="body"><div class="loading-center"><div class="spinner"></div></div></div>
      <div data-slot="pager" class="btn-row"></div>`;

    this.querySelector('.tab-group').addEventListener('click', e => {
      const t = e.target.closest('.tab');
      if (!t) return;
      this.#status = t.dataset.status;
      this.#offset = 0;
      this.querySelectorAll('.tab').forEach(x => x.setAttribute('aria-pressed', x === t ? 'true' : 'false'));
      this.#load();
    });
    this.querySelector('[data-f="severity"]').addEventListener('change', e => { this.#severity = e.target.value; this.#offset = 0; this.#load(); });
    this.querySelector('[data-f="module"]').addEventListener('change', e => { this.#module = e.target.value; this.#offset = 0; this.#load(); });
    this.querySelector('.page-header-actions').addEventListener('click', e => {
      const b = e.target.closest('button[data-all]');
      if (b) this.#all(b.dataset.all);
    });
    this.querySelector('[data-slot="body"]').addEventListener('click', e => {
      const b = e.target.closest('button[data-alert]');
      if (!b) return;
      const a = this.#items.find(x => String(x.id) === b.dataset.alert);
      if (!a) return;
      if (b.dataset.act === 'remove') this.#remove(a);
      else this.#one(a, b.dataset.act);
    });
    this.querySelector('[data-slot="pager"]').addEventListener('click', e => {
      const b = e.target.closest('button[data-page]');
      if (!b) return;
      this.#offset = Math.max(0, this.#offset + (b.dataset.page === 'next' ? PAGE : -PAGE));
      this.#load();
    });
    bindSortable(this, (key) => {
      const s = toggleSort({ key: this.#sortKey, dir: this.#sortDir }, key);
      this.#sortKey = s.key; this.#sortDir = s.dir;
      this.#render();
    });
    this.#load();
    this.#timer = setInterval(() => { if (!document.querySelector('.inspector-scrim.open')) this.#load(true); }, 30_000);
  }

  disconnectedCallback() { clearInterval(this.#timer); }

  #filter() {
    return { status: this.#status, severity: this.#severity, module: this.#module, ...this.#scope };
  }

  async #load(silent = false) {
    const body = this.querySelector('[data-slot="body"]');
    try {
      const res = await api.alerts.list({ ...this.#filter(), limit: PAGE, offset: this.#offset });
      this.#items = res.items ?? [];
      this.#total = res.total ?? 0;
      this.#render();
    } catch (err) {
      if (silent) return;
      body.innerHTML = `<div class="empty-state"><h3>Could not load</h3><p>${esc(err.message)}</p></div>`;
      this.querySelector('[data-slot="pager"]').innerHTML = '';
    }
  }

  async #one(a, status) {
    try {
      const res = await api.alerts.update(a.id, status);
      toast(res.lands?.says ?? 'Saved', 'success');
      this.#load(true);
    } catch (err) { toast(err.message, 'error'); }
  }

  async #all(status) {
    const scope = { ...this.#scope, module: this.#module };
    const n = (await api.alerts.list({ ...scope, status: 'open', limit: 1 }).catch(() => ({ total: 0 }))).total;
    if (!n) { toast('Nothing is open', 'info'); return; }
    const what = this.#module ? `every open ${this.#module} alert` : 'every open alert';
    const ok = await modalConfirm(`${status === 'resolved' ? 'Resolve' : 'Acknowledge'} ${what} — ${plural(n, 'alert')}?`,
      { title: status === 'resolved' ? 'Resolve all open' : 'Acknowledge all open', confirmText: status === 'resolved' ? 'Resolve them' : 'Acknowledge them', type: 'primary' });
    if (!ok) return;
    try {
      const res = await api.alerts.updateMany({ status, all: true, filter: { ...scope, status: 'open' } });
      toast(res.lands?.says ?? 'Done', 'success');
      this.#load(true);
    } catch (err) { toast(err.message, 'error'); }
  }

  async #remove(a) {
    if (!await modalConfirm(`Remove this alert from the record? "${a.title}"`, { title: 'Remove an alert', confirmText: 'Remove', type: 'danger' })) return;
    try {
      const res = await api.alerts.remove(a.id);
      toast(res.lands?.says ?? 'Removed', 'success');
      this.#load(true);
    } catch (err) { toast(err.message, 'error'); }
  }

  #render() {
    const body = this.querySelector('[data-slot="body"]');
    const pager = this.querySelector('[data-slot="pager"]');
    if (!this.#items.length) {
      body.innerHTML = `<div class="empty-state"><h3>No alerts</h3><p>${this.#status === 'open' ? 'Nothing is open.' : 'None match.'}</p></div>`;
      pager.innerHTML = '';
      return;
    }
    const rows = sortRows(this.#items, this.#columns, this.#sortKey, this.#sortDir);
    body.innerHTML = `
      <div class="card">
        <div class="table-wrapper">
          <table>
            <thead>${sortableHead(this.#columns, this.#sortKey, this.#sortDir)}</thead>
            <tbody>${rows.map(a => `
              <tr data-alert="${esc(a.id)}">
                <td class="nowrap">${severityTag(a.severity)}</td>
                <td class="nowrap">${badge('info', a.module)}</td>
                <td class="cell-fill">${esc(a.title)}${a.link
                  ? ` <a href="${esc(a.link)}" class="text-muted" data-link>details</a>` : ''}
                  ${a.detail && !String(a.detail).startsWith('{') ? `<div class="text-muted cell-clip" title="${esc(a.detail)}">${esc(String(a.detail).split('\n')[0].slice(0, 120))}</div>` : ''}</td>
                <td class="nowrap">${esc(a.subject ?? '')}</td>
                <td class="nowrap text-muted" title="${esc(formatDateTime(a.opened_at))}">${esc(timeAgo(a.opened_at))}</td>
                <td class="nowrap">${badge(a.status === 'open' ? 'warning' : a.status === 'resolved' ? 'ok' : 'info', a.status)}</td>
                <td class="nowrap text-muted">${a.resolved_at ? `resolved ${esc(timeAgo(a.resolved_at))}` : a.acknowledged_at ? `acknowledged ${esc(timeAgo(a.acknowledged_at))}` : ''}</td>
                <td class="text-right nowrap">
                  ${a.status === 'open' ? `<button type="button" class="btn btn-ghost btn-sm" data-alert="${esc(a.id)}" data-act="acknowledged">Acknowledge</button>` : ''}
                  ${a.status !== 'resolved' ? `<button type="button" class="btn btn-ghost btn-sm" data-alert="${esc(a.id)}" data-act="resolved">Resolve</button>`
                    : `<button type="button" class="btn btn-ghost btn-sm" data-alert="${esc(a.id)}" data-act="remove">Remove</button>`}</td>
              </tr>`).join('')}</tbody>
          </table>
        </div>
      </div>`;
    const pages = Math.max(1, Math.ceil(this.#total / PAGE));
    const cur = Math.floor(this.#offset / PAGE) + 1;
    pager.innerHTML = pages > 1 ? `
      <button type="button" class="btn btn-ghost btn-sm" data-page="prev" ${cur <= 1 ? 'disabled' : ''}>Newer</button>
      <span class="text-muted">page ${cur} of ${pages} · ${esc(plural(this.#total, 'alert'))}</span>
      <button type="button" class="btn btn-ghost btn-sm" data-page="next" ${cur >= pages ? 'disabled' : ''}>Older</button>`
      : `<span class="text-muted">${esc(plural(this.#total, 'alert'))}</span>`;
  }
}

customElements.define('arc-alerts', ArcAlerts);
